import { withMention } from '#plugin-chat/components/tools/helpers/withMention.ts';
import { type PlanInput, PlanInputSchema, type PlanOutput } from '#plugin-chat/components/tools/plan/schema.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';

/**
 * Structured planning tool. Agents call this before any multi-step workflow so the
 * plan is expressed as validated tool-call fields (summary, final grain, expected
 * output, ordered steps, assumptions) rather than JSON-shaped prose narration in a
 * regular assistant message.
 *
 * Migrated to the new `<Tool>` component as part of Phase 4b's tool framework
 * consolidation. Tool-mention references inside the description and result
 * message are now hardcoded as backtick-wrapped names — the new `<Tool>`
 * component sets `.mention` on the inner ToolImpl, so cross-tool references
 * are baked into prose rather than read off the function.
 *
 * The when-to/not-to-use bullets dropped during that migration are restored
 * on the `description` prop via a nested `<ToolGuidance>` (the new `<Tool>`
 * has no separate `instructions` channel); the Procedure list is carried as
 * its `children`.
 */
export const PlanTool = withMention('plan', function PlanToolImpl() {
  return (
    <Tool<PlanInput, PlanOutput>
      description={
        <ToolGuidance
          description="Declare the multi-step plan for answering the user's question. Call this before any other tool when the answer requires two or more tool calls."
          tool={PlanTool}
          whenNotToUse={[
            'Trivial single-call answers (one lookup, one clarifying question, a single dashboard of already-staged data).',
            'Questions a single `transformDataset` or `showDashboard` call can answer on its own.',
          ]}
          whenToUse={[
            'Any question that will require two or more tool calls to answer.',
            'Ambiguous or multi-step requests where stating the approach up front lets the user verify intent before data fetches start.',
          ]}
        >
          <header>Procedure</header>
          <ol>
            <li>
              Call this tool FIRST, before any data-fetching or processing tools. A plan after the fact is a failure
              mode.
            </li>
            <li>
              Each step <code>description</code> must be plain English — describe <em>what</em> the step does and{' '}
              <em>why</em>, never the tool arguments. The <code>tool</code> field is the only place a tool name belongs.
            </li>
            <li>
              Populate <code>finalGrain</code> and <code>expectedOutput</code> on every call. The plan must collapse raw
              data down to <code>finalGrain</code>, and the last step typically produces <code>expectedOutput</code> via
              `showDashboard`.
            </li>
            <li>
              Surface assumptions you are making (entity names, time ranges, unit conventions, categorical encodings) in{' '}
              <code>assumptions</code> instead of guessing silently.
            </li>
            <li>
              After this tool returns, execute the plan. Do not restate or re-narrate it in a normal assistant message —
              the tool result speaks for itself.
            </li>
          </ol>
        </ToolGuidance>
      }
      display={{
        running: 'Planning...',
        complete: { label: 'Execution plan created', semantic: 'plan' },
        error: 'Plan creation failed',
        // content: ({ args }: { args: PlanInput }) => {
        //   const { summary, steps, assumptions, finalGrain, expectedOutput } = args;
        //   return (
        //     <Stack gap="sm">
        //       {summary && (
        //         <div>
        //           <strong>{summary}</strong>
        //         </div>
        //       )}
        //       {!!steps?.length && (
        //         <div>
        //           <Divider label="Steps" labelPosition="left" />
        //           <List type="ordered">
        //             {steps.map((step, idx) => (
        //               // biome-ignore lint/suspicious/noArrayIndexKey: Plan steps render in a fixed order; index is stable.
        //               <List.Item key={idx}>
        //                 {step?.description}
        //                 {step?.tool && (
        //                   <Badge ml="xs" size="sm" variant="light">
        //                     {step.tool}
        //                   </Badge>
        //                 )}
        //               </List.Item>
        //             ))}
        //           </List>
        //         </div>
        //       )}
        //       {!!assumptions?.length && (
        //         <div>
        //           <Divider label="Assumptions" labelPosition="left" />
        //           <List type="unordered">
        //             {assumptions.map((assumption, idx) => (
        //               // biome-ignore lint/suspicious/noArrayIndexKey: Plan assumptions render in a fixed order; index is stable.
        //               <List.Item key={idx}>{assumption}</List.Item>
        //             ))}
        //           </List>
        //         </div>
        //       )}
        //       {finalGrain && <div>Final grain: {finalGrain}</div>}
        //       {expectedOutput && <div>Expected output: {expectedOutput}</div>}
        //     </Stack>
        //   );
        // },
        progress: 'planning...',
      }}
      execute={(): PlanOutput => ({ success: true as const })}
      message={{
        // Silence the call-side system message — the rendered plan in
        // `execute`'s result message already carries every field the LLM needs,
        // and re-emitting the raw args at call time would duplicate the
        // structured plan body in the transcript.
        call: () => (
          <p>
            Invoking the <code>plan</code> tool to prepare an execution plan...
          </p>
        ),
        result: ({ args }: { args: PlanInput }) => (
          <>
            <strong>
              Execution plan recorded ({args.steps.length} step{args.steps.length === 1 ? '' : 's'}, final grain:{' '}
              {args.finalGrain}).
            </strong>
            <p>{args.summary}</p>
            <header>Final grain</header>
            <p>{args.finalGrain}</p>
            <header>Expected output</header>
            <p>{args.expectedOutput}</p>
            <header>Steps</header>
            <ol>
              {args.steps.map((step) => (
                <li key={step.description}>
                  {step.description}
                  {step.tool ? (
                    <>
                      {' '}
                      (via <code>{step.tool}</code>)
                    </>
                  ) : null}
                </li>
              ))}
            </ol>
            {args.assumptions?.length ? (
              <>
                <header>Assumptions</header>
                <ul>
                  {args.assumptions.map((assumption) => (
                    <li key={assumption}>{assumption}</li>
                  ))}
                </ul>
              </>
            ) : null}
            <p>
              Begin executing step 1 now; do not wait for the user. Do not call the plan tool again until all steps are
              complete, and do not restate or re-narrate the plan in a normal assistant message — the tool result speaks
              for itself.
            </p>
          </>
        ),
      }}
      name="plan"
      parameters={PlanInputSchema}
    />
  );
});
